/**
 * Generato automaticamente.
 * framework-cmp-domande
 */
import { useMutation, useQuery } from 'react-query';
import type {
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseMutationOptions,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import { apiClientRequest } from '../../../client/apiClient.ts';
import type { ErrorType } from '../../../client/apiClient.ts';
import type { GetFrontVerificaCheckAndSendMailParams } from '../schemas/getFrontVerificaCheckAndSendMailParams.ts';
import type { GetFrontVerificaCheckMailParams } from '../schemas/getFrontVerificaCheckMailParams.ts';
import type { PostFrontVerificaTipoVerificaParams } from '../schemas/postFrontVerificaTipoVerificaParams.ts';
import type { VerificaResponse } from '../schemas/verificaResponse.ts';

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

// eslint-disable-next-line
type SecondParameter<T extends (...args: any) => any> = T extends (
  config: any,
  args: infer P,
) => any
  ? P
  : never;

/**
 * @summary Attiva una specifica tipologia di verifica
 */
export const postFrontVerificaTipoVerifica = (
  tipoVerifica: string,
  params: PostFrontVerificaTipoVerificaParams,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<VerificaResponse>(
    {
      url: `/framework-cmp-domande/verifica/${tipoVerifica}`,
      method: 'post',
      params,
    },
    options,
  );
};

export const getPostFrontVerificaTipoVerificaMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postFrontVerificaTipoVerifica>>,
    TError,
    { tipoVerifica: string; params: PostFrontVerificaTipoVerificaParams },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postFrontVerificaTipoVerifica>>,
  TError,
  { tipoVerifica: string; params: PostFrontVerificaTipoVerificaParams },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postFrontVerificaTipoVerifica>>,
    { tipoVerifica: string; params: PostFrontVerificaTipoVerificaParams }
  > = props => {
    const { tipoVerifica, params } = props ?? {};

    return postFrontVerificaTipoVerifica(tipoVerifica, params, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostFrontVerificaTipoVerificaMutationResult = NonNullable<
  Awaited<ReturnType<typeof postFrontVerificaTipoVerifica>>
>;

export type PostFrontVerificaTipoVerificaMutationError = ErrorType<unknown>;

/**
 * @summary Attiva una specifica tipologia di verifica
 */
export const usePostFrontVerificaTipoVerifica = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postFrontVerificaTipoVerifica>>,
    TError,
    { tipoVerifica: string; params: PostFrontVerificaTipoVerificaParams },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getPostFrontVerificaTipoVerificaMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * @summary Controlla se l'indirizzo email dell'utente è verificato
 */
export const getFrontVerificaCheckMail = (
  params: GetFrontVerificaCheckMailParams,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<boolean>(
    {
      url: `/framework-cmp-domande/verifica/checkMail`,
      method: 'get',
      params,
      signal,
    },
    options,
  );
};

export const getGetFrontVerificaCheckMailQueryKey = (
  params: GetFrontVerificaCheckMailParams,
) =>
  [
    `/framework-cmp-domande/verifica/checkMail`,
    ...(params ? [params] : []),
  ] as const;

export const getGetFrontVerificaCheckMailQueryOptions = <
  TData = Awaited<ReturnType<typeof getFrontVerificaCheckMail>>,
  TError = ErrorType<unknown>,
>(
  params: GetFrontVerificaCheckMailParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontVerificaCheckMail>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getFrontVerificaCheckMail>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetFrontVerificaCheckMailQueryKey(params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getFrontVerificaCheckMail>>
  > = ({ signal }) => getFrontVerificaCheckMail(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions };
};

export type GetFrontVerificaCheckMailQueryResult = NonNullable<
  Awaited<ReturnType<typeof getFrontVerificaCheckMail>>
>;
export type GetFrontVerificaCheckMailQueryError = ErrorType<unknown>;

/**
 * @summary Controlla se l'indirizzo email dell'utente è verificato
 */
export const useGetFrontVerificaCheckMail = <
  TData = Awaited<ReturnType<typeof getFrontVerificaCheckMail>>,
  TError = ErrorType<unknown>,
>(
  params: GetFrontVerificaCheckMailParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontVerificaCheckMail>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetFrontVerificaCheckMailQueryOptions(
    params,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * @summary Controlla se l'indirizzo email dell'utente è verificato altrimenti invia un email di verifica
 */
export const getFrontVerificaCheckAndSendMail = (
  params: GetFrontVerificaCheckAndSendMailParams,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<void>(
    {
      url: `/framework-cmp-domande/verifica/checkAndSendMail`,
      method: 'get',
      params,
      signal,
    },
    options,
  );
};

export const getGetFrontVerificaCheckAndSendMailQueryKey = (
  params: GetFrontVerificaCheckAndSendMailParams,
) =>
  [
    `/framework-cmp-domande/verifica/checkAndSendMail`,
    ...(params ? [params] : []),
  ] as const;

export const getGetFrontVerificaCheckAndSendMailQueryOptions = <
  TData = Awaited<ReturnType<typeof getFrontVerificaCheckAndSendMail>>,
  TError = ErrorType<unknown>,
>(
  params: GetFrontVerificaCheckAndSendMailParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontVerificaCheckAndSendMail>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getFrontVerificaCheckAndSendMail>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetFrontVerificaCheckAndSendMailQueryKey(params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getFrontVerificaCheckAndSendMail>>
  > = ({ signal }) =>
    getFrontVerificaCheckAndSendMail(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions };
};

export type GetFrontVerificaCheckAndSendMailQueryResult = NonNullable<
  Awaited<ReturnType<typeof getFrontVerificaCheckAndSendMail>>
>;
export type GetFrontVerificaCheckAndSendMailQueryError = ErrorType<unknown>;

/**
 * @summary Controlla se l'indirizzo email dell'utente è verificato altrimenti invia un email di verifica
 */
export const useGetFrontVerificaCheckAndSendMail = <
  TData = Awaited<ReturnType<typeof getFrontVerificaCheckAndSendMail>>,
  TError = ErrorType<unknown>,
>(
  params: GetFrontVerificaCheckAndSendMailParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontVerificaCheckAndSendMail>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetFrontVerificaCheckAndSendMailQueryOptions(
    params,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};
